import { useTheme } from 'next-themes';
import { toast, Toaster as Sonner } from 'sonner';

import { Button } from './button';
import { StripBanner } from './strip-banner';

type ToasterProps = React.ComponentProps<typeof Sonner>;

// Reuse StripBanner's own variant union so the bridge stays in sync with the
// component without StripBanner having to export an internal type.
type BannerToastVariant = NonNullable<React.ComponentProps<typeof StripBanner>['variant']>;

interface BannerToastOptions {
  /** Secondary text shown under the title; maps to StripBanner's `message`. */
  description?: React.ReactNode;
  /** Call-to-action button. Clicking it runs `onClick` and dismisses the toast. */
  action?: { label: string; onClick: () => void };
  /** Auto-dismiss duration in ms. Pass `Infinity` to keep the toast until dismissed. */
  duration?: number;
  /** Whether to show the dismiss (X) button. Defaults to true. */
  dismissible?: boolean;
}

interface BannerToastPromiseMessages<T> {
  /** Shown while the promise is pending. */
  loading: string;
  /** Shown on resolve; a function receives the resolved value. */
  success: string | ((data: T) => string);
  /** Shown on reject; a function receives the thrown error. */
  error: string | ((error: unknown) => string);
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      className="toaster group"
      // bannerToast renders a StripBanner that owns its own surface (background,
      // border, padding, shadow), so the toast container itself is transparent
      // and padding-less. `w-full` makes the banner fill the toaster's --width
      // even though unstyled mode drops Sonner's default width rule.
      toastOptions={{
        unstyled: true,
        classNames: { toast: 'group flex w-full bg-transparent p-0 shadow-none' },
      }}
      {...props}
    />
  );
};

/**
 * Renders transient toasts using the StripBanner design instead of Sonner's
 * default toast styling. Mirrors the Sonner API (`bannerToast.error(title,
 * { description })`) so call sites read the same. The toaster owns the live
 * region, so the rendered StripBanner intentionally omits its `role` prop.
 */
const bannerToast = {
  info: (title: string, options?: BannerToastOptions) =>
    showBannerToast('information', title, options),
  success: (title: string, options?: BannerToastOptions) =>
    showBannerToast('success', title, options),
  warning: (title: string, options?: BannerToastOptions) =>
    showBannerToast('warning', title, options),
  error: (title: string, options?: BannerToastOptions) =>
    showBannerToast('negative', title, options),
  neutral: (title: string, options?: BannerToastOptions) =>
    showBannerToast('neutral', title, options),
  promise: showBannerPromise,
  dismiss: toast.dismiss,
};

function showBannerToast(
  variant: BannerToastVariant,
  title: string,
  options: BannerToastOptions = {},
) {
  const { description, action, duration, dismissible = true } = options;

  return toast.custom(
    (id) => (
      <StripBanner
        variant={variant}
        layout="wrapped"
        className="w-full shadow-lg"
        title={title}
        message={description}
        action={
          action ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                action.onClick();
                toast.dismiss(id);
              }}
            >
              {action.label}
            </Button>
          ) : undefined
        }
        showDismiss={dismissible}
        onDismiss={() => toast.dismiss(id)}
      />
    ),
    { duration, unstyled: true, className: 'w-full' },
  );
}

// Drives a loading → success/error sequence with banner toasts. Unlike Sonner's
// native toast.promise, each phase is rendered as a StripBanner. Resolves to the
// awaited value, or undefined if the promise rejected (the error is surfaced as
// a toast rather than rethrown, so callers don't need a catch).
async function showBannerPromise<T>(
  input: Promise<T>,
  messages: BannerToastPromiseMessages<T>,
): Promise<T | undefined> {
  const loadingId = showBannerToast('information', messages.loading, {
    dismissible: false,
    duration: Infinity,
  });

  try {
    const data = await input;
    toast.dismiss(loadingId);
    showBannerToast(
      'success',
      typeof messages.success === 'function' ? messages.success(data) : messages.success,
    );
    return data;
  } catch (error) {
    toast.dismiss(loadingId);
    showBannerToast(
      'negative',
      typeof messages.error === 'function' ? messages.error(error) : messages.error,
    );
    return undefined;
  }
}

export { Toaster, bannerToast };
